// Mock gesture handler components for web compatibility
// This avoids React 19 findNodeHandle errors on web
import React from 'react';
import { View, ScrollView as RNScrollView, FlatList as RNFlatList, TextInput as RNTextInput, Text as RNText, Pressable as RNPressable } from 'react-native';

// Mock PanGestureHandler - passes through as View
export const PanGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock GestureHandlerRootView
export const GestureHandlerRootView = React.forwardRef(({ children, style, ...props }, ref) => (
  <View ref={ref} style={style} {...props}>
    {children}
  </View>
));

// Mock PinchGestureHandler
export const PinchGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock TapGestureHandler
export const TapGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock LongPressGestureHandler
export const LongPressGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock RotationGestureHandler
export const RotationGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock FlingGestureHandler
export const FlingGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock PressGestureHandler
export const PressGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock NativeViewGestureHandler
export const NativeViewGestureHandler = React.forwardRef(({ children, onGestureEvent, onHandlerStateChange, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock FlatList from gesture handler
export const FlatList = RNFlatList;

// Mock ScrollView from gesture handler
export const ScrollView = RNScrollView;

// Mock TextInput from gesture handler (used by @gorhom/bottom-sheet)
export const TextInput = RNTextInput;

// Mock Swipeable
export const Swipeable = React.forwardRef(({ children, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock DrawerLayout
export const DrawerLayout = React.forwardRef(({ children, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

// Mock GestureDetector (v2 API)
export const GestureDetector = React.forwardRef(({ children, gesture, ...props }, ref) => (
  <View ref={ref} {...props}>
    {children}
  </View>
));

const CHAINABLE_GESTURE_METHODS = [
  'enabled',
  'onStart',
  'onBegin',
  'onUpdate',
  'onChange',
  'onEnd',
  'onFinalize',
  'onTouchesDown',
  'onTouchesMove',
  'onTouchesUp',
  'onTouchesCancelled',
  'runOnJS',
  'withTestId',
  'withRef',
  'shouldCancelWhenOutside',
  'simultaneousWithExternalGesture',
  'requireExternalGestureToFail',
  'blocksExternalGesture',
  'manualActivation',
  'activeOffsetX',
  'activeOffsetY',
  'failOffsetX',
  'failOffsetY',
  'minPointers',
  'maxPointers',
  'minDistance',
  'minVelocity',
  'minVelocityX',
  'minVelocityY',
  'hitSlop',
  'numberOfTaps',
  'maxDuration',
  'maxDelay',
];

const createChainableGesture = () => {
  const gesture = {};
  CHAINABLE_GESTURE_METHODS.forEach((method) => {
    gesture[method] = () => gesture;
  });
  return gesture;
};

// Mock Gesture object (v2 API) — chainable so .enabled() / .onStart() etc. work on web
export const Gesture = {
  Pan: () => createChainableGesture(),
  Tap: () => createChainableGesture(),
  Pinch: () => createChainableGesture(),
  Rotation: () => createChainableGesture(),
  Fling: () => createChainableGesture(),
  LongPress: () => createChainableGesture(),
  Native: () => createChainableGesture(),
  Manual: () => createChainableGesture(),
  Race: () => createChainableGesture(),
  Simultaneous: () => createChainableGesture(),
  Exclusive: () => createChainableGesture(),
};

// Mock State constants
export const State = {
  UNDETERMINED: 0,
  FAILED: 1,
  BEGAN: 2,
  CANCELLED: 3,
  ACTIVE: 4,
  END: 5,
};

// Mock Direction constants
export const Direction = {
  RIGHT: 1,
  LEFT: 2,
  UP: 4,
  DOWN: 8,
};

// Mock enableLegacyWebImplementation (no-op on web)
export const enableLegacyWebImplementation = () => {
  // No-op for web - this is only needed for native
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 enableLegacyWebImplementation: no-op on web (using mocked gesture handler)');
  }
};

// Mock enableExperimentalWebImplementation (no-op on web)
export const enableExperimentalWebImplementation = () => {
  // No-op for web - this is only needed for native
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 enableExperimentalWebImplementation: no-op on web (using mocked gesture handler)');
  }
};

// Mock TouchableOpacity, TouchableHighlight, etc from gesture handler
export const TouchableOpacity = ({ children, ...props }) => (
  <View {...props}>
    {children}
  </View>
);

export const TouchableHighlight = ({ children, ...props }) => (
  <View {...props}>
    {children}
  </View>
);

export const TouchableWithoutFeedback = ({ children, ...props }) => (
  <View {...props}>
    {children}
  </View>
);

export const TouchableNativeFeedback = ({ children, ...props }) => (
  <View {...props}>
    {children}
  </View>
);

// Mock BorderlessButton and RectButton
export const BorderlessButton = ({ children, ...props }) => (
  <View {...props}>
    {children}
  </View>
);

export const RectButton = ({ children, ...props }) => (
  <View {...props}>
    {children}
  </View>
);

// Mock BaseButton (used by react-native-gifted-chat)
export const BaseButton = React.forwardRef(({ children, onPress, onActiveStateChange, ...props }, ref) => (
  <RNPressable 
    ref={ref} 
    onPress={onPress}
    onPressIn={() => onActiveStateChange?.(true)}
    onPressOut={() => onActiveStateChange?.(false)}
    {...props}
  >
    {children}
  </RNPressable>
));

// Export Text from react-native (react-native-gifted-chat expects it from gesture-handler)
export const Text = RNText;

// Export Pressable from react-native (react-native-gifted-chat expects it from gesture-handler)
export const Pressable = RNPressable;

// Default export
export default {
  PanGestureHandler,
  GestureHandlerRootView,
  PinchGestureHandler,
  TapGestureHandler,
  LongPressGestureHandler,
  RotationGestureHandler,
  FlingGestureHandler,
  PressGestureHandler,
  NativeViewGestureHandler,
  FlatList,
  ScrollView,
  TextInput,
  Swipeable,
  DrawerLayout,
  GestureDetector,
  Gesture,
  State,
  Direction,
  TouchableOpacity,
  TouchableHighlight,
  TouchableWithoutFeedback,
  TouchableNativeFeedback,
  BorderlessButton,
  RectButton,
  BaseButton,
  Text,
  Pressable,
  enableLegacyWebImplementation,
  enableExperimentalWebImplementation,
};
